import type {FetchOptions} from '../../@types/fetch'
import {useSetLogged} from '../states/auth'
import {ApiError} from '../utils/ApiError'
import {UserDisconnectedEvent} from '../utils/event'
import eventDispatcher from '../utils/eventDispatcher'
import {fetchApiResource} from '../utils/fetchApi'
import {createCollectionIri} from '../utils/iri'

import {useEvent} from './useEvent'
import {useFetcher} from './useFetcher'
import {useMutateResource} from './useMutate'

let currentUserIdentifier: string | null = null

export function useLogout(): () => void {
  const setLogged = useSetLogged()
  const {abortAll} = useFetcher()

  return useEvent(() => {
    currentUserIdentifier = null

    abortAll()

    void fetchApiResource(createCollectionIri('auth/logout'), {method: 'POST', credentials: 'include'})
      .catch(() => {/* best-effort */})

    setLogged(null)
  })
}

export function useLogin(): (userIdentifier: string) => Promise<void> {
  const setLogged = useSetLogged()
  const {abortAll} = useFetcher()
  const mutateResource = useMutateResource()

  return useEvent(async (userIdentifier: string) => {
    if (currentUserIdentifier === userIdentifier) {
      setLogged(userIdentifier)

      return
    }

    abortAll()
    currentUserIdentifier = userIdentifier
    // The cookie is already set: a failed revalidation must not leave the caller stuck as anonymous.
    await mutateResource(createCollectionIri('me'))
      .catch(() => {/* best-effort */})

    setLogged(userIdentifier)
  })
}

export function useAuthenticatedFetcher(): (key: string, init?: FetchOptions) => any {
  const logout = useLogout()
  const {fetcher} = useFetcher()

  return useEvent(async (resource: string, init?: FetchOptions): Promise<any> => {
    try {
      return await fetcher(resource, {...init, credentials: 'include'})
    } catch (e) {
      if (e instanceof ApiError && e.statusCode === 401) {
        eventDispatcher.dispatchEvent(new UserDisconnectedEvent())
        logout()
      }

      throw e
    }
  })
}
